/* eslint-disable react/no-find-dom-node */
import * as React from 'react'
import { findDOMNode, createPortal } from 'react-dom'
import classNames from 'classnames'

import ArrowContainer from './ArrowContainer'

const DEFAULT_POSITIONS = ['top', 'left', 'right', 'bottom']

const CONTAINER_CLASS_NAME = 'slite-popover-container color-mode-elevated'

const getContainerRect = container => {
  if (container === window || container === document.body) {
    return {
      top: window.pageYOffset,
      left: window.pageXOffset,
      width: window.innerWidth,
      height: window.innerHeight,
    }
  }

  return container.getBoundingClientRect()
}

const hasTargetRectChanged = (oldPositon, newPosition) =>
  !oldPositon ||
  oldPositon.left !== newPosition.left ||
  oldPositon.top !== newPosition.top ||
  oldPositon.width !== newPosition.width ||
  oldPositon.height !== newPosition.height

export default class TinyPopover extends React.PureComponent {
  static defaultProps = {
    isOpen: false,
    horizontalPadding: 6,
    verticalPadding: 6,
    windowBorderPadding: 6,
    position: ['top', 'right', 'left', 'bottom'],
    align: 'center',
    className: '',
    transitionDuration: 0.35,
    withArrow: false,
    arrowSize: 10,
    onClickOutside: () => true,
    onTooltipRef: () => true,
    container: document.body,
    onPopoverOpening: () => {},
  }

  targetRef = null
  tooltipRef = null
  closingTimeout = null

  state = { visible: false }

  static getDerivedStateFromProps(props, state) {
    if (props.isOpen && !state.visible) {
      return { visible: true }
    }

    return null
  }

  componentDidMount() {
    this.positionOrder = this.getPositionPriorityOrder()
    this.targetRef = findDOMNode(this)

    if (this.props.isOpen) {
      this.onOpen()
      this.forceUpdate()
    }
  }

  componentDidUpdate(prevProps) {
    const { isOpen } = this.props

    if (!prevProps.isOpen && isOpen) {
      this.onOpen()
    }

    if (prevProps.isOpen && !isOpen) {
      this.onClose()
      this.onClosing()
    }
  }

  componentWillUnmount() {
    this.onClose()
    clearTimeout(this.closingTimeout)
  }

  onTooltipRef = ref => {
    this.props.onTooltipRef(ref)

    if (!ref) {
      return
    }

    this.tooltipRef = ref
    this.props.isOpen && this.forceUpdate()
  }

  onOpen = () => {
    this.props.onPopoverOpening()
    window.addEventListener('mousedown', this.onWindowMouseDown, {
      passive: true,
    })
    window.addEventListener('wheel', this.onWheel, { passive: true })
    window.addEventListener('resize', this.onResize, { passive: true })
  }

  onClosing = () => {
    this.closingTimeout = setTimeout(() => {
      // TinyPopover could have been reopened in the meantime !
      if (this.props.isOpen) {
        return
      }

      this.tooltipRef = null
      this.setState({ visible: false })
    }, this.props.transitionDuration * 1000)
  }

  onClose = () => {
    window.removeEventListener('mousedown', this.onWindowMouseDown, {
      passive: true,
    })
    window.removeEventListener('wheel', this.onWheel)
    window.removeEventListener('resize', this.onResize)
  }

  onWindowMouseDown = event => {
    const { onClickOutside } = this.props
    if (
      (!this.targetRef || !this.targetRef.contains(event.target)) &&
      (!this.tooltipRef || !this.tooltipRef.contains(event.target))
    ) {
      onClickOutside(event)
    }
  }

  stopPropagation = event => {
    event.stopPropagation()
  }

  onWheel = event => {
    const newTargetRect = this.targetRef.getBoundingClientRect()

    if (hasTargetRectChanged(this.targetRect, newTargetRect)) {
      this.targetRect = newTargetRect
      this.forceUpdate()
    }
  }

  onResize = event => {
    if (!this.tooltipRef) {
      return
    }

    this.forceUpdate()
  }

  alignHorizontally = (
    { targetWidth, targetLeft, tooltipWidth },
    containerRect
  ) => {
    const { align, windowBorderPadding } = this.props

    let left = targetLeft + targetWidth / 2 - tooltipWidth / 2

    if (align === 'start') {
      left = targetLeft
    }

    if (align === 'end') {
      left = targetLeft + targetWidth - tooltipWidth
    }

    if (left - window.pageXOffset + containerRect.left < windowBorderPadding) {
      left = Math.min(
        windowBorderPadding + window.pageXOffset - containerRect.left,
        targetLeft + windowBorderPadding
      )
    }

    if (
      left - window.pageXOffset + containerRect.left + tooltipWidth >
      window.innerWidth - windowBorderPadding
    ) {
      left = Math.max(
        window.innerWidth -
          windowBorderPadding -
          tooltipWidth +
          window.pageXOffset -
          containerRect.left,
        targetLeft + targetWidth - tooltipWidth - windowBorderPadding
      )
    }

    return left
  }

  alignVertically = (
    { targetTop, targetHeight, tooltipHeight },
    containerRect
  ) => {
    const { align, windowBorderPadding } = this.props

    let top = targetTop + targetHeight / 2 - tooltipHeight / 2

    if (align === 'start') {
      top = targetTop
    }

    if (align === 'end') {
      top = targetTop + targetHeight - tooltipHeight
    }

    if (top - window.pageYOffset + containerRect.top < windowBorderPadding) {
      top = Math.min(
        windowBorderPadding + window.pageYOffset - containerRect.top,
        targetTop + windowBorderPadding
      )
    }

    if (
      top - window.pageYOffset + containerRect.top + tooltipHeight >
      window.innerHeight - windowBorderPadding
    ) {
      top = Math.max(
        window.innerHeight -
          windowBorderPadding -
          tooltipHeight +
          window.pageYOffset -
          containerRect.top,
        targetTop + targetHeight - tooltipHeight - windowBorderPadding
      )
    }

    return top
  }

  getPositionPriorityOrder() {
    const { position } = this.props
    const remainingPositions = DEFAULT_POSITIONS.filter(
      defaultPosition => defaultPosition !== position
    )

    return [position, ...remainingPositions]
  }

  getPosition = (targetRect, containerRect, index = 0) => {
    const defaultPosition = { top: 0, left: 0, opacity: 0 }
    const bypassCollision = index >= this.positionOrder.length

    if (!this.tooltipRef) {
      return defaultPosition
    }

    const currentIndex = bypassCollision ? 0 : index

    let top, left
    const {
      verticalPadding,
      horizontalPadding,
      withArrow,
      arrowSize,
      isOpen,
    } = this.props

    const verticalPaddings = verticalPadding + (withArrow ? arrowSize : 0)
    const horizontalPaddings = horizontalPadding + (withArrow ? arrowSize : 0)
    const position = this.positionOrder[currentIndex]

    const { width: targetWidth, height: targetHeight } = targetRect
    const [targetTop, targetLeft] = [
      targetRect.top - containerRect.top,
      targetRect.left - containerRect.left,
    ]
    const {
      width: tooltipWidth,
      height: tooltipHeight,
    } = this.tooltipRef.getBoundingClientRect()

    switch (position) {
      case 'top': {
        top = targetTop - tooltipHeight - verticalPaddings
        left = this.alignHorizontally(
          { targetWidth, targetLeft, tooltipWidth },
          containerRect
        )
        break
      }
      case 'bottom': {
        top = targetTop + targetHeight + verticalPaddings
        left = this.alignHorizontally(
          { targetWidth, targetLeft, tooltipWidth },
          containerRect
        )
        break
      }
      case 'left': {
        left = targetLeft - tooltipWidth - horizontalPaddings
        top = this.alignVertically(
          { targetHeight, targetTop, tooltipHeight },
          containerRect
        )
        break
      }
      case 'right': {
        left = targetLeft + targetWidth + horizontalPaddings
        top = this.alignVertically(
          { targetHeight, targetTop, tooltipHeight },
          containerRect
        )
        break
      }
      default:
        return defaultPosition
    }

    const tooltipPosition = {
      position,
      top,
      left,
      width: tooltipWidth,
      height: tooltipHeight,
      opacity: isOpen ? 1 : 0,
    }

    if (!bypassCollision && this.isColliding(tooltipPosition, containerRect)) {
      return this.getPosition(targetRect, containerRect, index + 1)
    }

    return tooltipPosition
  }

  isColliding({ top, left, width, height }, containerRect) {
    const { windowBorderPadding } = this.props
    const relativeTop = top - window.pageYOffset + containerRect.top
    const relativeLeft = left - window.pageXOffset + containerRect.left

    return (
      relativeTop < windowBorderPadding ||
      relativeTop + height > window.innerHeight - windowBorderPadding ||
      relativeLeft < windowBorderPadding ||
      relativeLeft + width > window.innerWidth - windowBorderPadding
    )
  }

  getContent = args => {
    const { content } = this.props

    return typeof content === 'function' ? content(args) : content
  }

  renderTooltipPortal() {
    if (!this.targetRef) {
      return null
    }

    const {
      className,
      containerStyle,
      transitionDuration,
      withArrow,
      arrowColor,
      arrowSize,
      arrowStyle,
      container,
    } = this.props

    const targetRect = this.targetRef.getBoundingClientRect()
    const containerRect = getContainerRect(container)
    const tooltipPosition = this.getPosition(targetRect, containerRect)
    const { top, left, opacity, position } = tooltipPosition

    const styles =
      typeof containerStyle === 'function'
        ? containerStyle(targetRect)
        : containerStyle

    return createPortal(
      <div
        ref={this.onTooltipRef}
        className={classNames(CONTAINER_CLASS_NAME, className)}
        style={{
          position: 'absolute',
          top,
          left,
          opacity,
          zIndex: 9,
          transition: `opacity ${transitionDuration}s`,
          ...styles,
        }}
        onMouseDown={this.stopPropagation}
        onClick={this.stopPropagation}
        onMouseOver={this.stopPropagation}
      >
        {this.getContent()}
        {withArrow &&
          this.tooltipRef && (
            <ArrowContainer
              position={position}
              arrowColor={arrowColor}
              arrowSize={arrowSize}
              style={arrowStyle}
              targetRect={targetRect}
              popoverRect={tooltipPosition}
            />
          )}
      </div>,
      container
    )
  }

  render() {
    const { children, isOpen } = this.props
    const { visible } = this.state

    return (
      <>
        {children}
        {(isOpen || visible) && this.renderTooltipPortal()}
      </>
    )
  }
}
